import { useEffect, useState, type FormEvent } from 'react';
import { Modal } from './Modal';

interface AdminPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (adminPassword: string) => void;
  error?: string | null;
  isLoading?: boolean;
}

export function AdminPasswordModal({
  isOpen,
  onClose,
  onSubmit,
  error,
  isLoading = false,
}: AdminPasswordModalProps): JSX.Element {
  const [adminPassword, setAdminPassword] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAdminPassword('');
    }
  }, [isOpen]);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (adminPassword.trim() && !isLoading) {
      onSubmit(adminPassword);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Become Admin">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-gray-600 dark:text-gray-400">
          Enter the admin password to manage this board.
        </p>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <div>
          <label
            htmlFor="adminPromotePassword"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Admin password
          </label>
          <input
            type="password"
            id="adminPromotePassword"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            placeholder="Enter admin password"
            autoFocus
            disabled={isLoading}
            autoComplete="current-password"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!adminPassword.trim() || isLoading}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Checking...' : 'Become Admin'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
